import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  ICreatePolicyProps,
  IPolicy,
  IUser,
  FieldType,
  IAppState,
} from 'types';
import styles from './styles/q4e.scss';
import usePager from 'hooks/usePager';
import history from 'helpers/history';
import {
  getOtherSubmitData,
  getDefaultValues,
  getEmployerOptions,
  createEmployerPolicies,
  updateUserEmployer,
  shouldSkipPage,
  getEmployerQuestions,
  getTitle,
  getFormTitle,
  getPage,
  getSubTitle,
} from './utils';
import { OTHER_EMPLOYER_ID } from 'constants/user';
import {
  PAGE_VIEW_EVENTS,
  findEmployerById,
  getEmployerPolicies,
  hasEmployerPolicy,
} from 'helpers';
import Title from 'components/title';
import { useDispatch, useSelector } from 'react-redux';
import {
  CreatePolicies,
  DeletePolicies,
  UpdatePolicy,
  UpdateUser,
  UpdateUserLoaded,
} from 'actions';
import Q4ERemove from './components/Q4ERemove';
import { AnimatedCheckScreen } from 'components/screens';
import { ToggleButton } from 'components/quashed';
import Q4EBenefits from './components/Q4EBenefits';
import useTracking from 'hooks/useTracking';

const employerSearch = (options, searchVal = '') => {
  const val = searchVal.trim().toLowerCase();
  return options.filter(
    (o) =>
      R.includes(val, o.text.toLowerCase()) || o.text === OTHER_EMPLOYER_ID,
  );
};

const Q4E = () => {
  useTracking(PAGE_VIEW_EVENTS.Q4EWorkplace);
  const dispatch = useDispatch();
  const {
    policies: { policies, loading: policiesLoading },
    referenceData: { employers, policyProviders },
    user,
  } = useSelector<any, IAppState>(
    R.pick(['policies', 'referenceData', 'user']),
  );
  const [isEdit, setEdit] = useState(
    !(user.employerId || user.otherEmployerName) || !user.q4eCompleted,
  );

  const createPolicies = (p: ICreatePolicyProps[]) =>
    dispatch(CreatePolicies(p));
  const updatePolicy = (p: IPolicy) => dispatch(UpdatePolicy(p));
  const updateUser = (u: IUser) => dispatch(UpdateUser(u));
  const updateUserLocal = (u: IUser) => dispatch(UpdateUserLoaded(u));
  const deletePolicies = useCallback(() => {
    const employer = findEmployerById(employers, user.employerId);
    const employerPolicies = getEmployerPolicies(policies, employer);
    const ids = R.pluck('id', employerPolicies);
    if (ids.length) {
      dispatch(DeletePolicies(ids));
    }
  }, [employers, policies, user.employerId]);

  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState(getDefaultValues(user));
  const [submittedOther, setSubmittedOther] = useState(false);

  const location = useLocation();
  const fromDashboard = R.pathOr(false, ['state', 'fromDashboard'], location);
  const skipPage = fromDashboard || shouldSkipPage();

  const { active, next, prev, resetActive } = usePager(skipPage ? 1 : 0, true);

  const employer = findEmployerById(employers, values.employerId);

  const isUpdate = useMemo(() => hasEmployerPolicy(policies, employer), [
    employer,
    policies,
  ]);

  // Update form values when user gets updated
  useEffect(() => {
    setValues(getDefaultValues(user));
  }, [
    user.employerId,
    user.employedBy,
    user.employmentStart,
    user.grade,
    user.income,
    user.otherEmployerName,
    user.q4eOption,
  ]);

  const selectedEmployerQuestions = useMemo(
    () => getEmployerQuestions(employers, values),
    [employers, values],
  );

  const reset = () => {
    resetActive();
    setEdit(false);
  };

  const submitEmployer = async (updatedUser: IUser) => {
    const submitData = updateUserEmployer(
      updateUser,
      employers,
      user,
      updatedUser,
    );
    await createEmployerPolicies(
      createPolicies,
      updatePolicy,
      employers,
      policies,
      submitData,
    );
  };

  const onSubmit = async (data) => {
    const updatedUser = R.merge(values, data);
    setValues(updatedUser);
    setLoading(true);

    const selectedEmployer = findEmployerById(
      employers,
      updatedUser.employerId,
    );

    if (active === 0) {
      // Need to save employerId if we require to verify email
      if (
        (!selectedEmployer?.policies?.length &&
          updatedUser.employerId !== OTHER_EMPLOYER_ID) ||
        selectedEmployer?.emailVerification
      ) {
        updateUserEmployer(updateUser, employers, user, updatedUser);
      }

      // If non-partnered employer, we finish and go to profile page
      if (
        !selectedEmployer?.policies?.length &&
        !selectedEmployer?.offers?.length &&
        updatedUser.employerId !== OTHER_EMPLOYER_ID
      ) {
        reset();
      } else {
        next();
      }
    } else if (active === 1 && selectedEmployerQuestions?.length > 0) {
      // If employer has questions, next (go to last page)
      // If other employer, update user
      if (updatedUser.employerId === OTHER_EMPLOYER_ID) {
        const submitData = getOtherSubmitData(user, updatedUser);
        updateUser(submitData);
        await deletePolicies();
      }
      next();
    } else {
      if (updatedUser.employerId === OTHER_EMPLOYER_ID) {
        setSubmittedOther(true);
      } else {
        await submitEmployer(updatedUser);
      }

      if (fromDashboard) {
        history.goBack();
      } else {
        reset();
      }
    }
    setLoading(false);
  };

  const options = useMemo(() => getEmployerOptions(employers), [employers]);

  const handleBack = () => {
    if (active === 0 || fromDashboard) {
      history.goBack();
    } else {
      prev();
    }
  };

  const handleRemove = () => {
    const updated = {
      ...user,
      employerId: undefined,
      employerName: undefined,
      workEmailVerified: false,
    };
    setValues(getDefaultValues(updated));
    updateUser(updated);
    deletePolicies();
  };

  const handleEdit = () => setEdit(true);

  const PageComponent = getPage(
    employer,
    selectedEmployerQuestions,
    active,
    values,
  );
  const title = getTitle(selectedEmployerQuestions, active, values, employer);
  const subTitle = getSubTitle(active, values, employer, isEdit);
  const formTitle = getFormTitle(employer, values, active);
  const questions = useMemo(() => {
    if (active === 0) {
      return [
        {
          key: 'employerId',
          placeholder: 'Search',
          labelAfter:
            'If your employer is not on the list simply tell us who you work for and we’ll reach out to them to check.',
          required: true,
          type: FieldType.Select,
          search: employerSearch,
          options,
          minCharacters: 2,
          showButton: true,
          children: employer && <Q4ERemove onRemove={handleRemove} />,
        },
      ];
    }
    return selectedEmployerQuestions;
  }, [active, selectedEmployerQuestions]);

  const handleAfterSubmitOther = () => {
    setSubmittedOther(false);
    reset();
  };

  if (submittedOther) {
    return <AnimatedCheckScreen onFinish={handleAfterSubmitOther} />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Title
          titleClassName={styles.title}
          subtitle={subTitle}
          title={title}
          tooltip={
            <>
              <b>
                Make the most of your workplace provided insurance benefits.
              </b>
              <br />
              Check to see if your employer provides you with free or discounted
              insurance.
            </>
          }
        />
        {!isEdit && !policiesLoading && (
          <ToggleButton
            className={styles.edit}
            toggled={isEdit}
            onClick={handleEdit}
          />
        )}
      </div>
      {isEdit ? (
        <PageComponent
          employer={employer}
          employers={employers}
          hideBack={active === 0 && !fromDashboard}
          policyProviders={policyProviders}
          values={values}
          user={user}
          isUpdate={isUpdate}
          loading={loading}
          questions={questions}
          title={formTitle}
          onBack={handleBack}
          onRemoveWorkplace={handleRemove}
          onSubmit={onSubmit}
          setValues={setValues}
          updateUser={updateUser}
          updateUserLocal={updateUserLocal}
          reset={reset}
          resetActive={resetActive}
        />
      ) : (
        <Q4EBenefits
          employer={employer}
          employers={employers}
          loading={policiesLoading}
          policies={policies}
          user={user}
          setEdit={setEdit}
          submitEmployer={submitEmployer}
        />
      )}
    </div>
  );
};

export default Q4E;
